<template>
  <div class="ask-ai-page">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">Ask AI Your Question</h1>
      <div :class="['balance-text', balanceClass]">
        Your balance: ₹{{ creditBalance }}
      </div>
    </div>

    <!-- Low credit banner with single CTA -->
    <div
      v-if="creditBalance > 0 && creditBalance < minBalance"
      class="low-credit-banner"
    >
      <p>Your credits are running low. Please add credits to continue.</p>
      <button class="btn-primary" @click="openModal">
        Add Credits
      </button>
    </div>

    <!-- Question Card -->
    <div class="question-card" :class="{ disabled: creditBalance <= 0 }">
      <textarea
        id="question"
        placeholder="Enter your question..."
        rows="12"
        maxlength="1000"
        v-model="question"
        :disabled="creditBalance <= 0"
      ></textarea>
      <p class="instructions">
        You can ask anything related to tax laws, judgments, or general queries.
      </p>
      <p class="char-limit">{{ question.length }}/1000</p>
      <p v-if="error" class="error-message">{{ error }}</p>
      <button
        class="login-button"
        @click="submitQuestion"
        :disabled="creditBalance <= 0"
      >
        {{ creditBalance <= 0 ? 'Out of Credits' : 'Submit' }}
      </button>
    </div>

        <!-- Credit + Details Modal -->
    <div v-if="showCreditModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-container">
        <header class="modal-header">
          <h3>Enter Your Details & Select Amount</h3>
        </header>
        <form class="modal-body" @submit.prevent="confirmAdd" novalidate>
          <!-- Full Name -->
          <div class="form-group">
            <label for="full-name">
              Full Name<span class="required">*</span>
            </label>
            <BaseInput
              id="full-name"
              v-model="customerName"
              placeholder="Your full name"
              required
            />
          </div>

          <!-- Address Line 1 -->
          <div class="form-group">
            <label for="address1">
              Address Line 1<span class="required">*</span>
            </label>
            <BaseInput
              id="address1"
              v-model="addressLine1"
              placeholder="House / Street"
              required
            />
          </div>

          <!-- Address Line 2 -->
          <div class="form-group">
            <label for="address2">Address Line 2</label>
            <BaseInput
              id="address2"
              v-model="addressLine2"
              placeholder="Area / Landmark"
            />
          </div>

          <!-- City -->
          <div class="form-group">
            <label for="city">
              City<span class="required">*</span>
            </label>
            <BaseInput
              id="city"
              v-model="city"
              placeholder="Your city"
              required
            />
          </div>

          <!-- Postal Code -->
          <div class="form-group">
            <label for="postal">
              Postal Code<span class="required">*</span>
            </label>
            <BaseInput
              id="postal"
              v-model="postalCode"
              placeholder="e.g. 560001"
              maxlength="6"
              required
            />
          </div>

          <!-- Mobile Number -->
          <div class="form-group">
            <label for="phone">
              Mobile Number<span class="required">*</span>
            </label>
            <BaseInput
              id="phone"
              type="tel"
              v-model="customerPhoneInput"
              placeholder="10-digit mobile number"
              required
            />
          </div>

          <!-- State -->
          <div class="form-group">
            <label for="state">
              State<span class="required">*</span>
            </label>
            <select id="state" v-model="stateCode" required>
              <option disabled value="">Select your state</option>
              <option v-for="s in stateOptions" :key="s.code" :value="s.code">
                {{ s.name }}
              </option>
            </select>
          </div>

          <!-- GST Number -->
          <div class="form-group">
            <label for="gst">GST Number (optional)</label>
            <BaseInput
              id="gst"
              v-model="gstNumber"
              placeholder="22AAAAA0000A1Z5"
            />
          </div>

          <!-- Credit Options -->
          <div class="form-group credit-options">
            <label>
              Select Amount<span class="required">*</span>
            </label>
            <div class="radio-group">
              <label
                v-for="option in creditOptions"
                :key="option.plan"
                class="radio-label"
              >
                <input
                  type="radio"
                  :value="option.plan"
                  v-model="selectedPlan"
                />
                ₹{{ option.amount }}
              </label>
            </div>
          </div>

          <p v-if="modalError" class="modal-error">{{ modalError }}</p>

          <footer class="modal-footer">
            <button type="button" class="btn-secondary" @click="closeModal">
              Cancel
            </button>
            <button type="submit" class="btn-primary">
              Add ₹{{ currentPlanAmount }}
            </button>
          </footer>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import BaseInput from '@/components/ui/BaseInput.vue';

export default {
  name: 'AskAIPage',
  components: { BaseInput },
  data() {
    return {
      question:          '',
      error:             '',
      creditOptions: [
        { plan: 'CREDITS_2500',  amount: 2500 },
        { plan: 'CREDITS_5000',  amount: 5000 },
        { plan: 'CREDITS_7500',  amount: 7500 },
        { plan: 'CREDITS_10000', amount:10000 }
      ],
      showCreditModal:   false,
      selectedPlan:      'CREDITS_2500',
      minBalance:        1100,

      // customer‐details form
      customerPhoneInput: '',
      customerName:       '',
      addressLine1:       '',
      addressLine2:       '',
      city:               '',
      postalCode:         '',
      stateCode:          '',
      gstNumber:          '',
      modalError:         '',
      contactType: 'customer',       // <= default that will always go
      customerSubType: 'business',   // <= default that will always go

      cfClient:          null,

      // full list of Indian states
      stateOptions: [
          { code: 'AP', name: 'Andhra Pradesh' },
        { code: 'AR', name: 'Arunachal Pradesh' },
        { code: 'AS', name: 'Assam' },
        { code: 'BR', name: 'Bihar' },
        { code: 'CG', name: 'Chhattisgarh' },
        { code: 'GA', name: 'Goa' },
        { code: 'GJ', name: 'Gujarat' },
        { code: 'HR', name: 'Haryana' },
        { code: 'HP', name: 'Himachal Pradesh' },
        { code: 'JH', name: 'Jharkhand' },
        { code: 'KA', name: 'Karnataka' },
        { code: 'KL', name: 'Kerala' },
        { code: 'MP', name: 'Madhya Pradesh' },
        { code: 'MH', name: 'Maharashtra' },
        { code: 'MN', name: 'Manipur' },
        { code: 'ML', name: 'Meghalaya' },
        { code: 'MZ', name: 'Mizoram' },
        { code: 'NL', name: 'Nagaland' },
        { code: 'OR', name: 'Odisha' },
        { code: 'PB', name: 'Punjab' },
        { code: 'RJ', name: 'Rajasthan' },
        { code: 'SK', name: 'Sikkim' },
        { code: 'TN', name: 'Tamil Nadu' },
        { code: 'TG', name: 'Telangana' },
        { code: 'TR', name: 'Tripura' },
        { code: 'UP', name: 'Uttar Pradesh' },
        { code: 'UT', name: 'Uttarakhand' },
        { code: 'WB', name: 'West Bengal' }
      ]
    };
  },
  computed: {
    creditBalance() {
      const v = parseFloat(localStorage.getItem('credit_balance'));
      return isNaN(v) ? 0 : v;
    },
    balanceClass() {
      if (this.creditBalance < 50)   return 'red-balance';
      if (this.creditBalance < 100)  return 'yellow-balance';
      return 'green-balance';
    },
    currentPlanAmount() {
      const o = this.creditOptions.find(x => x.plan === this.selectedPlan);
      return o ? o.amount : 0;
    }
  },
  methods: {
    async fetchProfile() {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await api.get('/auth/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Prefill saved data
        this.customerPhoneInput = res.data.mobile_number   || '';
        this.customerName       = res.data.full_name       || '';
        const addr = (res.data.billing_address || '').split('\n');
        this.addressLine1 = addr[0] || '';
        this.addressLine2 = addr[1] || '';
        this.city         = addr[2] || '';
        this.postalCode   = addr[3] || '';
        this.stateCode    = res.data.state      || '';
        this.gstNumber    = res.data.gst_number      || '';
        
        //console.log('[fetchProfile] full user data:', res.data);

      } catch (e) {
        console.error('[fetchProfile] error:', e);
      }
    },
    openModal() {
      this.error          = '';
      this.modalError     = '';
      this.selectedPlan   = 'CREDITS_2500';
      this.fetchProfile();
      this.showCreditModal = true;
    },
    closeModal() {
      this.showCreditModal = false;
    },
    async confirmAdd() {
      // normalize phone
      let raw = this.customerPhoneInput.trim().replace(/\D/g,'');
      if (raw.length > 10) raw = raw.slice(-10);

        // debug: dump everything
  console.log({
    fullName:     this.customerName,
    address1:     this.addressLine1,
    city:         this.city,
    postalCode:   this.postalCode,
    phone:        raw,
    stateCode:    this.stateCode
  });

      this.modalError = '';
      if (
        !this.customerName.trim() ||
        !this.addressLine1.trim() ||
        !this.city.trim() ||
        raw.length !== 10 ||
        !this.stateCode
      ) {
        this.modalError = 'Please fill in all required fields correctly.';
        return;
      }

      const payload = {
      plan:           this.selectedPlan,
      fullName:       this.customerName.trim(),
      addressLine1:   this.addressLine1.trim(),
      addressLine2:   this.addressLine2.trim() || null,
      city:           this.city.trim(),
      postalCode:     this.postalCode.trim(),
      customerPhone:  raw,
      stateCode:      this.stateCode,
      gstNumber:      this.gstNumber.trim() || null,
      contactType:     this.contactType,
      customerSubType: this.customerSubType
    };


      let session;
      try {
        const res = await api.post('/create-payment-session', payload);
        session = res.data;
      } catch (err) {
        console.error('createPaymentSession error:', err.response?.data || err);
        this.modalError = err.response?.data?.error || 'Payment start failed.';
        return;
      }

      try {
        if (!this.cfClient) {
          this.cfClient = await window.Cashfree({ mode: 'sandbox' });
        }
        const result = await this.cfClient.checkout({
          paymentSessionId: session.paymentSessionId,
          returnUrl: `${process.env.VUE_APP_FRONTEND_URL}/payment-success?order_id=${session.orderId}`
        });
        if (result.error) {
          throw result.error;
        }
      } catch (e) {
        console.error('Cashfree checkout error:', e);
        this.modalError = e.message || 'Checkout failed.';
        return;
      }

      this.closeModal();
    },
    submitQuestion() {
      this.error = '';
      if (!this.question.trim()) {
        this.error = 'Please enter a question before submitting.';
        return;
      }
      localStorage.setItem('user_question', this.question.trim());
      this.$router.push('/context');
    }
  },
  async mounted() {
    if (!localStorage.getItem('auth_token')) {
      this.$router.push('/login');
      return;
    }
    await this.fetchProfile();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

/* ─── Base Ask-AI Page Styles ─────────────────────────────────────────────── */
.ask-ai-page {
  padding: var(--space-md);
  max-width: var(--content-max-width, 1200px);
  margin: 0 auto;
  font-family: var(--font-sans, 'Poppins', sans-serif);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
}

.page-title {
  font-size: var(--font-size-xl, 1.5rem);
  color: var(--color-text, #1f2937);
}

.balance-text {
  font-size: var(--font-size-base, 1rem);
  font-weight: 500;
}
.red-balance    { color: var(--color-danger, #dc2626); }
.yellow-balance { color: var(--color-warning, #d97706); }
.green-balance  { color: var(--color-success, #16a34a); }

.low-credit-banner {
  background: var(--color-warning-light, #fff4e5);
  border: 1px solid var(--color-warning, #d97706);
  padding: var(--space-md);
  border-radius: var(--radius-sm);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-lg);
}

.question-card {
  background: var(--color-bg, #ffffff);
  padding: var(--space-lg, 1.5rem);
  border-radius: var(--radius-lg, 0.5rem);
  box-shadow: var(--shadow-md, 0 4px 6px rgba(0,0,0,0.1));
}
.question-card.disabled {
  opacity: 0.6;
}

textarea {
  width: 100%;
  padding: var(--space-sm, 0.5rem);
  border: 1px solid var(--color-muted, #e5e7eb);
  border-radius: var(--radius-sm, 0.25rem);
  resize: none;
  margin-bottom: var(--space-sm, 0.5rem);
}

.instructions,
.char-limit {
  margin-top: var(--space-sm, 0.5rem);
  font-size: var(--font-size-sm, 0.875rem);
}

.error-message {
  color: var(--color-danger, #dc2626);
  margin-top: var(--space-sm, 0.5rem);
}

.login-button {
  width: 100%;
  padding: var(--space-sm, 0.75rem) var(--space-md, 1rem);
  font-size: var(--font-size-base, 1rem);
  font-weight: 600;
  border: none;
  border-radius: var(--radius-sm);
  background-color: var(--color-accent, #3b82f6);
  color: white;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}
.login-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.login-button:hover:not(:disabled) {
  background-color: #2563eb;
}

/* ─── Modal Styles ───────────────────────────────────────────────────────── */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;            /* breathing room */
  overflow: auto;           /* overlay scroll if needed */
  z-index: 1000;
}

.modal-container {
  background: #fff;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;         /* fit in viewport */
  display: flex;
  flex-direction: column;
}

/* header */
.modal-header {
  background: var(--color-accent);
  color: #fff;
  padding: var(--space-lg);
  text-align: center;
}
.modal-header h3 {
  margin: 0;
  font-size: var(--font-size-lg, 1.25rem);
}

/* body */
.modal-body {
  padding: var(--space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  overflow-y: auto;         /* scrollable content */
  flex: 1;
}

/* ensure form elements fill width */
.modal-body select,
.modal-body input,
.modal-body textarea {
  width: 100%;
  font-family: inherit;
}

/* select styling */
.modal-body select {
  padding: var(--space-sm);
  border: 1px solid var(--color-muted);
  border-radius: var(--radius-sm);
  background: #fff;
  color: var(--color-text);
  appearance: none;
}

/* form groups */
.form-group {
  display: flex;
  flex-direction: column;
}
.form-group label {
  margin-bottom: var(--space-xxs);
  font-weight: 500;
}
.form-group textarea {
  resize: vertical;
}

/* radio groups */
.credit-options .radio-group,
.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  margin-top: var(--space-sm);
}
.radio-label {
  display: flex;
  align-items: center;
  gap: var(--space-xxs);
}

/* required field indicator */
.required {
  color: var(--color-danger);
  margin-left: 0.25em;
}

/* error text */
.modal-error {
  color: var(--color-danger);
  text-align: center;
  margin-top: var(--space-sm);
}

/* footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-sm);
  padding: var(--space-lg);
  background: var(--color-muted-light);
}

/* buttons */
.btn-primary {
  background: var(--color-accent);
  color: #fff;
  padding: var(--space-sm) var(--space-md);
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: opacity 0.2s ease;
}
.btn-secondary {
  background: var(--color-muted);
  color: var(--color-text);
  padding: var(--space-sm) var(--space-md);
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: opacity 0.2s ease;
}
.btn-primary:hover,
.btn-secondary:hover {
  opacity: 0.9;
}

/* ─── Responsiveness ─────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }
}
@media (max-width: 480px) {
  .modal-container {
    max-width: 100%;
  }
}
</style>
